import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import StatsSection from './components/StatsSection';
import TestimonialsSection from './components/TestimonialsSection';
import PricingSection from './components/PricingSection';
import HowItWorksSection from './components/HowItWorksSection';
import LandingFooter from './components/LandingFooter';

const NordPassLandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Tjenester', href: '#features' },
    { label: 'Priser', href: '#pricing' },
    { label: 'Slik fungerer det', href: '#how-it-works' },
    { label: 'Kontakt', href: '/nord-pass-contact-page' }
  ];

  return (
    <>
      <Helmet>
        <title>NordPass — Naviger Norges Veier med Trygghet</title>
        <meta name="description" content="Norges ledende veikart, bomkalkulator og kjøretøyadministrasjon. Sanntids trafikk, AutoPASS-rabatter og full kontroll — fra Nordkapp til Lindesnes." />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div
        style={{
          background: '#050A12',
          minHeight: '100vh',
          overflowX: 'hidden'
        }}
      >
        {/* Sticky nav bar */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(5,10,18,0.95)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: '0 5%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '64px'
            }}
          >
            {/* Logo */}
            <a
              href="/nord-pass-landing-page"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textDecoration: 'none',
                flexShrink: 0
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  background: '#3B6FD4',
                  clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))'
                }}
              />
              <span
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 800,
                  color: '#EEF0F2',
                  letterSpacing: '-0.03em'
                }}
              >
                NordPass
              </span>
            </a>

            {/* Desktop Nav links */}
            <div className="landing-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
              {navLinks?.map((link, i) => (
                <a
                  key={i}
                  href={link?.href}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    color: 'rgba(238,240,242,0.65)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(238,240,242,0.65)'; }}
                >
                  {link?.label}
                </a>
              ))}
            </div>

            {/* Desktop CTA buttons */}
            <div className="landing-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <a
                href="/login"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.875rem',
                  color: 'rgba(238,240,242,0.65)',
                  textDecoration: 'none',
                  padding: '8px 16px',
                  transition: 'color 150ms ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(238,240,242,0.65)'; }}
              >
                Logg inn
              </a>
              <a
                href="/login"
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: '#EEF0F2',
                  textDecoration: 'none',
                  padding: '10px 20px',
                  background: '#3B6FD4',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  transition: 'background 150ms ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#2A5BC4'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#3B6FD4'; }}
              >
                Start gratis
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              className="landing-mobile-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Åpne meny"
              style={{
                display: 'none',
                background: 'none',
                border: '1.5px solid rgba(255,255,255,0.15)',
                padding: '8px',
                cursor: 'pointer',
                color: '#EEF0F2',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {mobileMenuOpen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                  <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"/>
                  <line x1="3" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"/>
                  <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"/>
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div
              style={{
                background: 'rgba(5,10,18,0.98)',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                padding: '16px 5% 24px'
              }}
            >
              {navLinks?.map((link, i) => (
                <a
                  key={i}
                  href={link?.href}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'block',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1rem',
                    color: 'rgba(238,240,242,0.8)',
                    textDecoration: 'none',
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  {link?.label}
                </a>
              ))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
                <a
                  href="/login"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9375rem',
                    color: 'rgba(238,240,242,0.65)',
                    textDecoration: 'none',
                    padding: '12px 0',
                    textAlign: 'center',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  Logg inn
                </a>
                <a
                  href="/login"
                  style={{
                    fontFamily: 'Syne, sans-serif',
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    color: '#EEF0F2',
                    textDecoration: 'none',
                    padding: '14px 0',
                    background: '#3B6FD4',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    textAlign: 'center'
                  }}
                >
                  Start gratis
                </a>
              </div>
            </div>
          )}

          <style>{`
            @media (max-width: 767px) {
              .landing-desktop-nav { display: none !important; }
              .landing-desktop-cta { display: none !important; }
              .landing-mobile-toggle { display: flex !important; }
            }
          `}</style>
        </nav>

        {/* Page sections */}
        <HeroSection />

        <div id="features">
          <FeaturesSection />
        </div>

        <StatsSection />

        <TestimonialsSection />

        <div id="pricing">
          <PricingSection />
        </div>

        <div id="how-it-works">
          <HowItWorksSection />
        </div>

        <LandingFooter />
      </div>
    </>
  );
};

export default NordPassLandingPage;
